import { createContext, useContext, useRef, useState, type PropsWithChildren } from 'react'
import Confetti from 'react-confetti-boom'
import { Dimensions, Pressable, View } from 'react-native'
import { useThrottledCallback } from 'use-debounce'
import { cn } from '~/lib/utils'

const ConfettiContext = createContext<{
  showConfetti: ({ key, x, y }: { key?: string; x: number; y: number }) => void
  hideConfetti: () => void
}>({
  showConfetti: () => {},
  hideConfetti: () => {},
})

export default function ConfettiProvider({ children }: { children: React.ReactNode }) {
  const [confetti, setConfetti] = useState<{
    key: string
    x: number
    y: number
  } | null>(null)

  const showConfetti = ({ key, x, y }: { key?: string; x: number; y: number }) => {
    const confettiKey = key || Math.random().toString(36).substring(2, 15)
    setConfetti({ key: confettiKey, x, y })
  }

  const hideConfetti = () => {
    setConfetti(null)
  }

  return (
    <ConfettiContext.Provider value={{ showConfetti, hideConfetti }}>
      {children}
      {confetti && (
        <View className="pointer-events-none fixed inset-0 z-1000 select-none">
          <Confetti
            key={confetti.key}
            x={confetti?.x || 0.5}
            y={confetti?.y || 0.5}
            mode="boom"
            particleCount={100}
            shapeSize={15}
            deg={270}
            effectCount={1}
            spreadDeg={80}
            launchSpeed={0.5}
            colors={['#05FF97', '#efefef', '#119860', '#9e4eff', '#ababab']}
          />
        </View>
      )}
    </ConfettiContext.Provider>
  )
}

export function useConfetti() {
  return useContext(ConfettiContext)
}

const windowWidth = Dimensions.get('window').width
const windowHeight = Dimensions.get('window').height

export function ConfettiTrigger({
  children,
  className,
  isActive = true,
  confettiKey,
  timeout = 700,
}: PropsWithChildren<{
  className?: string
  isActive?: boolean
  confettiKey: string
  timeout?: number
}>) {
  const { showConfetti } = useConfetti()
  const confettiNode = useRef<View | null>(null)
  const confettiRef = useRef<{ x: number; y: number } | null>(null)

  const setConfettiPosition = (onSet?: (x: number, y: number) => void) => {
    if (!confettiNode.current) {
      return onSet?.(confettiRef.current?.x ?? 0, confettiRef.current?.y ?? 0)
    }

    // Get the bounding rectangle of the element
    confettiNode.current.measure((x, y, width, height, pageX, pageY) => {
      // Calculate the center of the element
      const centerX = pageX + width / 2
      const centerY = pageY + height / 2

      // Convert to viewport relative coordinates (0-1)
      const viewportX = centerX / windowWidth
      const viewportY = centerY / windowHeight

      confettiRef.current = { x: viewportX, y: viewportY }
      onSet?.(viewportX, viewportY)
    })
  }

  const debouncedShowConfetti = useThrottledCallback(() => {
    setConfettiPosition((x, y) =>
      showConfetti({
        x,
        y,
      })
    )
  }, 1000)

  return (
    <Pressable
      ref={(node) => {
        confettiNode.current = node

        if (node && isActive && !confettiRef.current) {
          // Show confetti after a short delay
          setTimeout(
            () =>
              setConfettiPosition((x, y) =>
                showConfetti({
                  key: confettiKey,
                  x,
                  y,
                })
              ),
            timeout
          )
        }
      }}
      className={cn(
        'flex-none transition-transform duration-150 hover:scale-110 active:scale-95',
        className
      )}
      onPress={debouncedShowConfetti}>
      {children}
    </Pressable>
  )
}
